"use client"

import { addShippingAddress } from "@lib/data"
import { CheckCircleSolid } from "@medusajs/icons"
import { IconBadge } from "@medusajs/ui"
import { FormEvent, useEffect, useState } from "react"
import { sendData } from "./actions"
import { redirect, RedirectType, useRouter } from "next/navigation"
import {
  AddressFormProps,
  AddressWhisper,
  BillingAddressForm,
  DeliveryAddressForm,
  ItemAddressWhisper,
} from "types/address"
import { useFormState } from "react-dom"
import { Status } from "types/global"
import { revalidatePath } from "next/cache"
import BillingAddress from "./components/biling-address"
import DeliveryAddress from "./components/delivery-address"
import { LuLamp } from "react-icons/lu"
import {
  addCustomerBillingDeliveryAddress,
  WhisperAddress,
} from "@lib/util/address-utils"

const AddressForm = () => {
  const [handleRedirect, setHandleRedirect] = useState<boolean>(false)
  const [displayConfirmation, setDisplayConfirmation] = useState(false)
  const router = useRouter()
  const [addCustomerAddressResponse, addCustomerAddressAction] = useFormState(
    sendData,
    undefined
  )

  //  Billing address states
  const [formBillingAddressData, setFormBillingAddressData] =
    useState<BillingAddressForm>({
      address: "",
      branch: "",
      company: "",
      dic: "",
      email: "",
      ico: "",
      phone: "",
    })
  const [addressWhisper, setAddressWhisper] = useState<ItemAddressWhisper[]>([])
  const [addressSelected, setAddressSelected] = useState<string>()
  const [fullAddressSelected, setFullAddressSelected] =
    useState<ItemAddressWhisper>({} as ItemAddressWhisper)
  const [addressShowWhisper, setAddressShowWhisper] = useState(false)

  const [billingAsDelivery, setBillingAsDelivery] = useState(true)

  // Delivery address states
  const [formDeliveryAddressData, setFormDeliveryAddressData] =
    useState<DeliveryAddressForm>({
      address: "",
      company: "",
      email: "",
      phone: "",
    })
  const [deliveryAddressWhisper, setDeliveryAddressWhisper] = useState<
    ItemAddressWhisper[]
  >([])
  const [deliveryAddressSelected, setDeliveryAddressSelected] =
    useState<string>()
  const [deliveryAddressShowWhisper, setDeliveryAddressShowWhisper] =
    useState(false)

  const [deliveryFullAddressSelected, setDeliveryFullAddressSelected] =
    useState<ItemAddressWhisper>({} as ItemAddressWhisper)

  // Billing address effects
  useEffect(() => {
    if (formBillingAddressData) {
      if (formBillingAddressData.address != addressSelected) {
        setFullAddressSelected({} as ItemAddressWhisper)
        if (
          formBillingAddressData &&
          formBillingAddressData.address.length >= 3
        ) {
          setAddressShowWhisper(true)
          WhisperAddress(formBillingAddressData.address).then(
            (responseWhisperData) => {
              setAddressWhisper(responseWhisperData.items)
            }
          )
        } else {
          if (addressShowWhisper) {
            setAddressShowWhisper(false)
          }
        }
      }
    }
  }, [formBillingAddressData])

  // Delivery address effects
  useEffect(() => {
    if (formDeliveryAddressData) {
      if (formDeliveryAddressData.address != deliveryAddressSelected) {
        setDeliveryFullAddressSelected({} as ItemAddressWhisper)
        if (
          formDeliveryAddressData &&
          formDeliveryAddressData.address.length >= 3
        ) {
          setDeliveryAddressShowWhisper(true)
          WhisperAddress(formDeliveryAddressData.address).then(
            (responseWhisperData) => {
              setDeliveryAddressWhisper(responseWhisperData.items)
            }
          )
        } else {
          if (deliveryAddressShowWhisper) {
            setDeliveryAddressShowWhisper(false)
          }
        }
      }
    }
  }, [formDeliveryAddressData])

  // Other things
  const [customerWroteAddress, setCustomerWroteAddress] = useState<string>("")

  const [deliveryAddressData, setDeliveryAddressData] = useState<string>("")

  // useEffect(() => {
  //   if (addCustomerAddressResponse?.status == Status.SUCCESS) {
  //     setDisplayConfirmation(true)
  //     setTimeout(() => {
  //       router.refresh()
  //     }, 1500)
  //   }
  // }, [addCustomerAddressResponse])

  const handleFormSubmit = () => {
    const valid = billingAsDelivery
      ? validateBillingForm()
      : validateDeliveryForm() && validateBillingForm()

    if (!valid) {
      alert("Vyplňte prosím všechny údaje")
    } else {
      addCustomerBillingDeliveryAddress(
        formBillingAddressData,
        fullAddressSelected,
        formDeliveryAddressData,
        deliveryFullAddressSelected,
        billingAsDelivery
      ).then((response) => {
        if (response) {
          setDisplayConfirmation(true)
          setHandleRedirect(true)
        } else {
          alert("Něco se pokazilo, zkuste to prosím později!")
          setDisplayConfirmation(false)
          setHandleRedirect(false)
        }
      })
    }
  }

  useEffect(() => {
    if (handleRedirect) {
      setTimeout(() => {
        router.refresh()
      }, 1000)
    }
  }, [handleRedirect])

  const validateBillingForm = () => {
    if (
      formBillingAddressData &&
      formBillingAddressData.company &&
      formBillingAddressData.branch &&
      formBillingAddressData.address &&
      formBillingAddressData.phone &&
      formBillingAddressData.email &&
      formBillingAddressData.ico &&
      formBillingAddressData.dic &&
      !addressShowWhisper &&
      formBillingAddressData.address.length >= 3 &&
      validateEmail(formBillingAddressData.email)
    ) {
      return true
    }
    return false
  }

  const validateDeliveryForm = () => {
    if (
      formDeliveryAddressData.company &&
      formDeliveryAddressData.address &&
      formDeliveryAddressData.phone &&
      formDeliveryAddressData.email &&
      !deliveryAddressShowWhisper &&
      formDeliveryAddressData.address.length >= 3 &&
      validateEmail(formDeliveryAddressData.email)
    ) {
      return true
    }

    return false
  }

  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  }

  return (
    <>
      {!displayConfirmation ? (
        <div className="bg-white flex flex-col items-center justify-center border-2 border-[#e5e7eb] rounded-lg p-5 w-fit">
          <h1 className="pb-3 text-2xl font-semibold text-center">
            Doplňte si prosím své informace
          </h1>
          <div className="flex flex-col">
            <BillingAddress
              shownData={formBillingAddressData}
              showWhisper={addressShowWhisper}
              whisperAddress={addressWhisper}
              onWhisperSelected={(addressWhisperSelected) => {
                const parsedAddress = `${addressWhisperSelected.name}, ${
                  addressWhisperSelected.regionalStructure.find(
                    (i) => i.type == "regional.municipality"
                  )?.name
                } ${addressWhisperSelected.zip}`

                setFormBillingAddressData((prev) => ({
                  ...prev,
                  address: parsedAddress,
                }))

                setFullAddressSelected(addressWhisperSelected)

                setAddressSelected(parsedAddress)

                setTimeout(() => {
                  setAddressShowWhisper(false)
                }, 100)
              }}
              onDataChange={(data) => setFormBillingAddressData(data)}
              setCheckBillingAsDelivery={(checked) =>
                setBillingAsDelivery(checked)
              }
              checkBillinggAsDelivery={billingAsDelivery}
            />
            <DeliveryAddress
              shownData={formDeliveryAddressData}
              showWhisper={deliveryAddressShowWhisper}
              whisperAddress={deliveryAddressWhisper}
              onWhisperSelected={(addressWhisperSelected) => {
                const parsedAddress = `${addressWhisperSelected.name}, ${
                  addressWhisperSelected.regionalStructure.find(
                    (i) => i.type == "regional.municipality"
                  )?.name
                } ${addressWhisperSelected.zip}`

                setFormDeliveryAddressData((prev) => ({
                  ...prev,
                  address: parsedAddress,
                }))

                setDeliveryFullAddressSelected(addressWhisperSelected)

                setDeliveryAddressSelected(parsedAddress)

                setTimeout(() => {
                  setDeliveryAddressShowWhisper(false)
                }, 100)
              }}
              onDataChange={(data) => setFormDeliveryAddressData(data)}
              showForm={!billingAsDelivery}
            />
          </div>
          <input
            type="submit"
            value="Uložit údaje"
            className="w-full col-span-2 p-2 font-semibold text-center text-white bg-green-800 rounded-lg hover:bg-green-900 hover:cursor-pointer"
            onClick={() => handleFormSubmit()}
          />
        </div>
      ) : (
        <div className="bg-white flex flex-row items-center justify-center border-2 border-[#e5e7eb] rounded-lg p-5 gap-x-3">
          {/* <IconBadge color={"green"} size={"large"}> */}
          <CheckCircleSolid color="#166534" />
          {/* </IconBadge> */}
          <h1>Údaje byly úspěšně uloženy</h1>
        </div>
      )}
    </>
  )
}

export default AddressForm
